import { ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

import Input from './Input';

type InputProps = ComponentProps<typeof Input>;

interface PasswordInputProps extends Omit<InputProps, 'type' | 'isShow' | 'unit'> {
    /** Affiche sous le champ les critères du mot de passe, cochés en direct. */
    showRules?: boolean;
}

/**
 * Critères alignés sur `Password::min(8)->letters()->numbers()` côté backend
 * (ChangeMyPasswordRequest, SignupRequest, StoreUserRequest).
 */
export const PASSWORD_RULES = [
    { key: 'min_length', test: (value: string) => value.length >= 8 },
    { key: 'letter', test: (value: string) => /\p{L}/u.test(value) },
    { key: 'number', test: (value: string) => /\p{N}/u.test(value) },
] as const;

interface PasswordEyeButtonProps {
    isVisible: boolean;
    onToggle: () => void;
    className?: string;
}

/** Bouton œil réutilisable (aussi sur l'écran de connexion, dont le champ n'est pas un Input). */
export function PasswordEyeButton({ isVisible, onToggle, className = '' }: PasswordEyeButtonProps) {
    const { t } = useTranslation();
    const label = t(isVisible ? 'password_input.hide' : 'password_input.show');

    return (
        <button
            type="button"
            onClick={onToggle}
            aria-label={label}
            aria-pressed={isVisible}
            title={label}
            className={`rounded-md p-1.5 text-text-color-light hover:bg-light-bg hover:text-text-color ${className}`}
        >
            {isVisible ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
        </button>
    );
}

/** Critères du mot de passe cochés en direct (aussi sous le champ de l'écran d'invitation). */
export function PasswordRules({ value }: { value: string }) {
    const { t } = useTranslation();

    return (
        <ul aria-label={t('password_input.rules_label')} className="mt-2 space-y-1 text-xs">
            {PASSWORD_RULES.map(({ key, test }) => {
                const isMet = test(value);
                return (
                    <li
                        key={key}
                        data-met={isMet}
                        className={`flex items-center gap-1.5 ${isMet ? 'text-success-color' : 'text-text-color-light'}`}
                    >
                        <CheckCircleIcon className={`h-4 w-4 ${isMet ? '' : 'opacity-40'}`} aria-hidden="true" />
                        <span>{t(`password_input.${key}`)}</span>
                        <span className="sr-only">
                            {t(isMet ? 'password_input.met' : 'password_input.unmet')}
                        </span>
                    </li>
                );
            })}
        </ul>
    );
}

export default function PasswordInput({ showRules = false, className, value, ...inputProps }: PasswordInputProps) {
    const [isVisible, setIsVisible] = useState(false);
    const text = typeof value === 'string' ? value : '';

    return (
        <div>
            <div className="relative">
                <Input
                    {...inputProps}
                    value={value}
                    type={isVisible ? 'text' : 'password'}
                    className={`pr-10 ${className ?? ''}`}
                />
                <PasswordEyeButton
                    isVisible={isVisible}
                    onToggle={() => setIsVisible((current) => !current)}
                    className="absolute right-1 top-[3px]"
                />
            </div>
            {showRules && <PasswordRules value={text} />}
        </div>
    );
}
